/**
 * FarmProfileEditor — owner-facing editor for a farm's rich profile.
 *
 * Route: /food-pulse/farm/edit/:slug
 * Backend:
 *   GET   /api/farms/profile/:slug     (public — used to prefill)
 *   PATCH /api/farms/profile/:slug     (auth — owner or admin)
 *
 * Sections:
 *   1. Basics (tagline, story, year_established, land_size_acres)
 *   2. Badges (multi-select from valid list)
 *   3. Growing methods (freeform chips)
 *   4. Seasonal calendar (12 month inputs)
 *   5. Farm store (on-farm shop toggle + hours + address note + external URL)
 *   6. CSA (toggle + external sign-up URL)
 *   7. Media (extra photo URLs, video URLs)
 *   8. Markets attended (multi-select from fp_farmers_markets)
 *   9. Visits toggle
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Sprout, Award, Calendar, Store, HeartHandshake,
  Image as ImageIcon, Video, MapPin, Plus, X, Loader2, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

const VALID_BADGES = [
  { key: "certified_organic",        label: "Certified Organic" },
  { key: "family_owned",             label: "Family Owned" },
  { key: "woman_owned",              label: "Woman Owned" },
  { key: "small_farm_under_5_acres", label: "Small Farm (< 5 acres)" },
  { key: "veteran_owned",            label: "Veteran Owned" },
  { key: "regenerative",             label: "Regenerative" },
  { key: "biodynamic",               label: "Biodynamic" },
  { key: "pasture_raised",           label: "Pasture Raised" },
  { key: "grass_fed",                label: "Grass Fed" },
  { key: "heirloom_varieties",       label: "Heirloom Varieties" },
  { key: "no_spray",                 label: "No Spray" },
];

const MONTHS = [
  { key: "jan", label: "January"   }, { key: "feb", label: "February" },
  { key: "mar", label: "March"     }, { key: "apr", label: "April"    },
  { key: "may", label: "May"       }, { key: "jun", label: "June"     },
  { key: "jul", label: "July"      }, { key: "aug", label: "August"   },
  { key: "sep", label: "September" }, { key: "oct", label: "October"  },
  { key: "nov", label: "November"  }, { key: "dec", label: "December" },
];

const TXT_INPUT = "w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2";

export default function FarmProfileEditor() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [biz, setBiz] = useState(null);
  const [availableMarkets, setAvailableMarkets] = useState([]);

  const [form, setForm] = useState({
    tagline: "",
    story: "",
    year_established: "",
    land_size_acres: "",
    badges: [],
    growing_methods: [],
    seasonal_calendar: {},
    csa_available: false,
    csa_url: "",
    farm_visits_ok: false,
    farmstand_open: false,
    farm_store_open: false,
    farm_store_pickup_hours: "",
    farm_store_address_note: "",
    farm_store_external_shop_url: "",
    media_photos: [],
    media_videos: [],
    markets_attended: [],
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/farms/profile/${slug}`).catch(() => ({ data: null })),
      axios.get(`${API}/api/farmers-markets`, { params: { limit: 200 } }).catch(() => ({ data: { items: [] } })),
    ]).then(([b, m]) => {
      if (b.data) {
        setBiz(b.data);
        const f = b.data.farm || {};
        const fs = f.farm_store || {};
        setForm({
          tagline:                       f.tagline || "",
          story:                         f.story || b.data.description || "",
          year_established:              f.year_established || "",
          land_size_acres:               f.land_size_acres || "",
          badges:                        f.badges || [],
          growing_methods:               f.growing_methods || [],
          seasonal_calendar:             f.seasonal_calendar || {},
          csa_available:                 !!f.csa_available,
          csa_url:                       f.csa_url || "",
          farm_visits_ok:                !!f.farm_visits_ok,
          farmstand_open:                !!f.farmstand_open,
          farm_store_open:               !!fs.is_open,
          farm_store_pickup_hours:       fs.pickup_hours || "",
          farm_store_address_note:       fs.address_note || "",
          farm_store_external_shop_url:  fs.external_shop_url || "",
          media_photos:                  f.media_photos || [],
          media_videos:                  f.media_videos || [],
          markets_attended:              f.markets_attended || [],
        });
      }
      setAvailableMarkets(m.data.items || []);
    }).finally(() => setLoading(false));
  }, [slug]);

  const toggleBadge = (b) =>
    setForm((p) => ({
      ...p,
      badges: p.badges.includes(b) ? p.badges.filter((x) => x !== b) : [...p.badges, b],
    }));

  const toggleMarket = (mSlug) =>
    setForm((p) => ({
      ...p,
      markets_attended: p.markets_attended.includes(mSlug)
        ? p.markets_attended.filter((x) => x !== mSlug)
        : [...p.markets_attended, mSlug],
    }));

  const addChip = (key, value) => {
    const v = (value || "").trim();
    if (!v) return;
    setForm((p) => ({ ...p, [key]: p[key].includes(v) ? p[key] : [...p[key], v] }));
  };
  const removeChip = (key, value) =>
    setForm((p) => ({ ...p, [key]: p[key].filter((x) => x !== value) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        is_farm:            true,
        tagline:            form.tagline || null,
        story:              form.story || null,
        year_established:   form.year_established ? parseInt(form.year_established, 10) : null,
        land_size_acres:    form.land_size_acres ? parseFloat(form.land_size_acres) : null,
        badges:             form.badges,
        growing_methods:    form.growing_methods,
        seasonal_calendar:  form.seasonal_calendar,
        csa_available:      form.csa_available,
        csa_url:            form.csa_url || null,
        farm_visits_ok:     form.farm_visits_ok,
        farmstand_open:     form.farmstand_open,
        farm_store: {
          is_open:            form.farm_store_open,
          pickup_hours:       form.farm_store_pickup_hours || null,
          address_note:       form.farm_store_address_note || null,
          external_shop_url:  form.farm_store_external_shop_url || null,
        },
        media_photos:       form.media_photos,
        media_videos:       form.media_videos,
        markets_attended:   form.markets_attended,
      };
      const token = localStorage.getItem("wofy_token") || localStorage.getItem("token");
      await axios.patch(`${API}/api/farms/profile/${slug}`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success("Farm profile saved");
      setTimeout(() => navigate(`/food-pulse/farm/${slug}`), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center" data-testid="farm-editor-login-gate">
          <Sprout className="w-14 h-14 mx-auto mb-3" style={{ color: AMBER }} />
          <h1 className="font-display text-3xl mb-2">Farm profile editor</h1>
          <p className="opacity-80 mb-6">Sign in with the account that claimed this farm to edit its profile.</p>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }}>
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
        <Loader2 className="w-10 h-10 animate-spin opacity-40" data-testid="farm-editor-loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="farm-editor-page">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Link to={`/food-pulse/businesses/${slug}`} className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-3">
          <ArrowLeft className="w-3 h-3" /> Back to business
        </Link>
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>— Farmstand editor</div>
            <h1 className="font-display text-3xl sm:text-4xl">{biz?.name || slug}</h1>
            <p className="opacity-70 text-sm mt-1">Tell shoppers what makes your farm special. Every field is optional — start with the story.</p>
          </div>
          <div className="flex gap-2">
            <Link to={`/food-pulse/farm/${slug}`} target="_blank"
                  className="rounded-full px-4 py-2 text-sm border" style={{ borderColor: GREEN, color: GREEN }}
                  data-testid="farm-editor-preview">
              Preview <ExternalLink className="w-3 h-3 inline ml-1" />
            </Link>
            <button onClick={save} disabled={saving}
                    className="rounded-full px-6 py-2 text-sm font-semibold text-white flex items-center gap-2"
                    style={{ background: GREEN }}
                    data-testid="farm-editor-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save profile
            </button>
          </div>
        </div>

        {/* Basics */}
        <Section icon={Sprout} title="Basics">
          <label className="block text-sm mb-3">
            <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Tagline</div>
            <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                   placeholder="Small-batch veggies grown with care" className={TXT_INPUT}
                   style={{ borderColor: "#E9D8B4" }} data-testid="farm-tagline" />
          </label>
          <label className="block text-sm mb-3">
            <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Our story</div>
            <textarea rows={6} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })}
                      placeholder="Share how the farm started, what you grow, and why it matters."
                      className={TXT_INPUT} style={{ borderColor: "#E9D8B4" }} data-testid="farm-story" />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Year established</div>
              <input type="number" value={form.year_established} onChange={(e) => setForm({ ...form, year_established: e.target.value })}
                     placeholder="1978" className={TXT_INPUT} style={{ borderColor: "#E9D8B4" }} data-testid="farm-year" />
            </label>
            <label className="block text-sm">
              <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Land size (acres)</div>
              <input type="number" step="0.1" value={form.land_size_acres} onChange={(e) => setForm({ ...form, land_size_acres: e.target.value })}
                     placeholder="4.5" className={TXT_INPUT} style={{ borderColor: "#E9D8B4" }} data-testid="farm-acres" />
            </label>
          </div>
        </Section>

        {/* Badges */}
        <Section icon={Award} title="Badges" subtitle="Tap the ones that describe your farm.">
          <div className="flex flex-wrap gap-2" data-testid="farm-badges">
            {VALID_BADGES.map((b) => {
              const on = form.badges.includes(b.key);
              return (
                <button key={b.key} type="button" onClick={() => toggleBadge(b.key)}
                        className="rounded-full px-3 py-1.5 text-xs font-semibold border-2 transition-all"
                        style={{ borderColor: on ? GREEN : "#E9D8B4",
                                 background: on ? GREEN : "transparent",
                                 color: on ? "#fff" : GREEN }}
                        data-testid={`badge-${b.key}`}>
                  {b.label}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Growing methods */}
        <Section icon={Sprout} title="Growing methods" subtitle="Chip list — press Enter to add.">
          <ChipsInput values={form.growing_methods}
                      onAdd={(v) => addChip("growing_methods", v)}
                      onRemove={(v) => removeChip("growing_methods", v)}
                      placeholder="e.g. organic, no-till, cover-cropping"
                      testId="growing-methods" />
        </Section>

        {/* Seasonal calendar */}
        <Section icon={Calendar} title="Seasonal calendar" subtitle="What's in season each month?">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="seasonal-calendar">
            {MONTHS.map((m) => (
              <label key={m.key} className="block text-sm">
                <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">{m.label}</div>
                <input value={form.seasonal_calendar[m.key] || ""}
                       onChange={(e) => setForm({
                         ...form,
                         seasonal_calendar: { ...form.seasonal_calendar, [m.key]: e.target.value },
                       })}
                       placeholder="Tomatoes, corn, beans"
                       className={TXT_INPUT} style={{ borderColor: "#E9D8B4" }}
                       data-testid={`month-${m.key}`} />
              </label>
            ))}
          </div>
        </Section>

        {/* Farm store */}
        <Section icon={Store} title="Farm store / farmstand"
                 subtitle="Do customers visit your farm directly?">
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={form.farm_store_open}
                   onChange={(e) => setForm({ ...form, farm_store_open: e.target.checked })}
                   data-testid="farm-store-open" />
            Yes — we have an on-farm shop open to visitors
          </label>
          {form.farm_store_open && (
            <div className="space-y-3">
              <label className="block text-sm">
                <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Pickup hours</div>
                <input value={form.farm_store_pickup_hours}
                       onChange={(e) => setForm({ ...form, farm_store_pickup_hours: e.target.value })}
                       placeholder="Sat 9-12, Sun 10-2" className={TXT_INPUT}
                       style={{ borderColor: "#E9D8B4" }} data-testid="farm-store-hours" />
              </label>
              <label className="block text-sm">
                <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Directions / notes</div>
                <textarea rows={2} value={form.farm_store_address_note}
                          onChange={(e) => setForm({ ...form, farm_store_address_note: e.target.value })}
                          placeholder="Look for the red barn off County Road 12. Follow the signs from the gate."
                          className={TXT_INPUT} style={{ borderColor: "#E9D8B4" }} data-testid="farm-store-note" />
              </label>
              <label className="block text-sm">
                <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">External shop URL (Shopify, Etsy, own site)</div>
                <input value={form.farm_store_external_shop_url}
                       onChange={(e) => setForm({ ...form, farm_store_external_shop_url: e.target.value })}
                       placeholder="https://yourfarm.com/shop" className={TXT_INPUT}
                       style={{ borderColor: "#E9D8B4" }} data-testid="farm-store-url" />
              </label>
            </div>
          )}
        </Section>

        {/* CSA */}
        <Section icon={HeartHandshake} title="CSA — Community Supported Agriculture">
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={form.csa_available}
                   onChange={(e) => setForm({ ...form, csa_available: e.target.checked })}
                   data-testid="csa-available" />
            We offer CSA shares
          </label>
          {form.csa_available && (
            <label className="block text-sm">
              <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Sign-up URL (external)</div>
              <input value={form.csa_url} onChange={(e) => setForm({ ...form, csa_url: e.target.value })}
                     placeholder="https://yourfarm.com/csa" className={TXT_INPUT}
                     style={{ borderColor: "#E9D8B4" }} data-testid="csa-url" />
              <div className="text-xs opacity-60 mt-1">In-app CSA checkout coming soon — for now, link to your existing sign-up.</div>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" checked={form.farm_visits_ok}
                   onChange={(e) => setForm({ ...form, farm_visits_ok: e.target.checked })}
                   data-testid="farm-visits" />
            Farm visits welcome (by appointment)
          </label>
        </Section>

        {/* Media */}
        <Section icon={ImageIcon} title="Media" subtitle="Add photo and video URLs. Press Enter to add.">
          <div className="mb-3">
            <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Extra photos (URLs)</div>
            <ChipsInput values={form.media_photos}
                        onAdd={(v) => addChip("media_photos", v)}
                        onRemove={(v) => removeChip("media_photos", v)}
                        placeholder="https://..." testId="media-photos" />
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Videos (YouTube / Vimeo URLs)</div>
            <ChipsInput values={form.media_videos}
                        onAdd={(v) => addChip("media_videos", v)}
                        onRemove={(v) => removeChip("media_videos", v)}
                        placeholder="https://youtube.com/watch?v=..." testId="media-videos" icon={Video} />
          </div>
        </Section>

        {/* Markets attended */}
        <Section icon={MapPin} title="Markets you attend"
                 subtitle="Select the farmers markets where you sell.">
          {availableMarkets.length === 0 ? (
            <div className="text-sm opacity-60">No markets available yet.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto" data-testid="markets-attended">
              {availableMarkets.map((m) => {
                const on = form.markets_attended.includes(m.slug);
                return (
                  <button key={m.slug} type="button" onClick={() => toggleMarket(m.slug)}
                          className="text-left rounded-xl border-2 p-3 transition-all"
                          style={{ borderColor: on ? GREEN : "#E9D8B4",
                                   background: on ? "#F0F5F1" : "transparent" }}
                          data-testid={`market-toggle-${m.slug}`}>
                    <div className="font-medium text-sm">{m.name}</div>
                    <div className="text-xs opacity-70">{[m.city, m.state].filter(Boolean).join(", ")}</div>
                  </button>
                );
              })}
            </div>
          )}
        </Section>

        <div className="flex justify-end sticky bottom-4 mt-8">
          <button onClick={save} disabled={saving}
                  className="rounded-full px-8 py-3 text-sm font-semibold text-white flex items-center gap-2 shadow-lg"
                  style={{ background: GREEN }}
                  data-testid="farm-editor-save-bottom">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save farm profile
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Ico, title, subtitle, children }) {
  return (
    <section className="rounded-3xl bg-white border p-6 mb-6" style={{ borderColor: "#E9D8B4" }}>
      <div className="flex items-baseline gap-2 mb-1">
        <Ico className="w-4 h-4" style={{ color: GREEN }} />
        <h2 className="font-display text-xl">{title}</h2>
      </div>
      {subtitle && <p className="text-xs opacity-70 mb-4">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      {children}
    </section>
  );
}

function ChipsInput({ values, onAdd, onRemove, placeholder, testId, icon: Ico }) {
  const [v, setV] = useState("");
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2" data-testid={testId}>
        {values.map((x) => (
          <span key={x} className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs"
                style={{ background: "#F0F5F1", color: GREEN }}>
            {Ico && <Ico className="w-3 h-3" />}
            <span className="max-w-[220px] truncate">{x}</span>
            <button type="button" onClick={() => onRemove(x)} className="opacity-50 hover:opacity-100">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={v} onChange={(e) => setV(e.target.value)}
               onKeyDown={(e) => {
                 if (e.key === "Enter") { e.preventDefault(); onAdd(v); setV(""); }
               }}
               placeholder={placeholder}
               className="flex-1 rounded-xl border px-3 py-2 text-sm outline-none"
               style={{ borderColor: "#E9D8B4" }}
               data-testid={`${testId}-input`} />
        <button type="button" onClick={() => { onAdd(v); setV(""); }}
                className="rounded-full px-3 py-2 text-sm font-semibold text-white"
                style={{ background: GREEN }}
                data-testid={`${testId}-add`}>
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
